import { useEffect, useState } from "react";
import { adminApi } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Loader2, UploadCloud, Wand2, Save, FolderOpen } from "lucide-react";

const NUM_FIELDS = [
  "sale_cash_today", "sale_year1_cash", "sale_year5_cash", "sale_tax_net_cash",
  "mgmt_net_today", "mgmt_year1_net", "mgmt_year5_net", "mgmt_year5_cmv", "mgmt_families_5yr",
  "frac_cash_today", "frac_year1_cash", "frac_year1_revenues", "frac_year1_appreciation",
  "frac_year5_cash", "frac_year5_revenues", "frac_year5_appreciation", "frac_families_5yr",
];

const initialModel = {
  property_name: "",
  contact_email: "",
  contact_first_name: "",
  contact_last_name: "",
  notes: "",
  ...Object.fromEntries(NUM_FIELDS.map((k) => [k, ""])),
};

const usd = (v) => (v === "" || v === null ? null : Number(v));
const sum3 = (a, b, c) => {
  const vals = [a, b, c].map(usd).filter((v) => v !== null && !isNaN(v));
  return vals.length ? vals.reduce((s, v) => s + v, 0) : null;
};

export const FinancialModelForm = ({ hsConfigured, defaultEmail, scoutBrief }) => {
  const [m, setM] = useState(initialModel);
  const [pushing, setPushing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [drafts, setDrafts] = useState([]);
  const [selectedDraft, setSelectedDraft] = useState("");

  useEffect(() => {
    adminApi
      .get("/admin/financial-model/drafts")
      .then((r) => setDrafts(r.data || []))
      .catch(() => {});
  }, []);

  const cleanPayload = () => {
    const payload = { ...m };
    NUM_FIELDS.forEach((k) => {
      if (payload[k] === "") delete payload[k];
      else payload[k] = Number(payload[k]);
    });
    Object.keys(payload).forEach((k) => {
      if (payload[k] === "" || payload[k] === null) delete payload[k];
    });
    return payload;
  };

  const saveDraft = async () => {
    if (!m.property_name.trim()) {
      toast.error("Enter the property name before saving.");
      return;
    }
    setSaving(true);
    try {
      const r = await adminApi.post("/admin/financial-model/draft", cleanPayload());
      setDrafts((d) => [r.data, ...d]);
      toast.success("Sheet saved — it will be here whenever you come back.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not save the sheet.");
    } finally {
      setSaving(false);
    }
  };

  const loadDraft = (id) => {
    setSelectedDraft(id);
    const d = drafts.find((x) => x.id === id);
    if (!d) return;
    const next = { ...initialModel };
    Object.keys(initialModel).forEach((k) => {
      if (d[k] !== undefined && d[k] !== null) next[k] = String(d[k]);
    });
    setM(next);
    toast.success(`Loaded sheet: ${d.property_name}`);
  };

  const email = m.contact_email || defaultEmail || "";
  const set = (k) => (e) => setM((f) => ({ ...f, [k]: e.target.value }));

  const canPrefill = Boolean(scoutBrief?.asking_price_usd);

  const prefill = () => {
    const price = Number(scoutBrief.asking_price_usd);
    const rent = Number(scoutBrief.monthly_rent_usd) || 0;
    const netRentYr = rent * 12 * 0.9; // after Janté 10% management fee
    const r = (v) => String(Math.round(v));
    setM((f) => ({
      ...f,
      property_name: f.property_name || scoutBrief.address || scoutBrief.market || "",
      sale_cash_today: r(price),
      sale_year1_cash: r(price),
      sale_year5_cash: r(price),
      sale_tax_net_cash: r(price * 0.75),
      mgmt_net_today: "0",
      mgmt_year1_net: r(netRentYr),
      mgmt_year5_net: r(netRentYr * 5),
      mgmt_year5_cmv: r(price * Math.pow(1.1, 5)),
      frac_cash_today: r(price * 0.5),
      frac_year1_cash: r(price * 0.5),
      frac_year1_revenues: r(netRentYr * 0.5),
      frac_year1_appreciation: r(price * 0.5 * 0.1),
      frac_year5_cash: r(price * 0.5),
      frac_year5_revenues: r(netRentYr * 0.5 * 5),
      frac_year5_appreciation: r(price * 0.5 * (Math.pow(1.1, 5) - 1)),
      notes:
        f.notes ||
        "Pre-filled from Sasha's Property Scout brief — assumptions: 10% Janté management fee, 10% annual appreciation, 25% capital gains on sale. Review before pushing.",
    }));
    toast.success("Financial model pre-filled from the Scout brief — review the numbers.");
  };

  const y1Net = sum3(m.frac_year1_cash, m.frac_year1_revenues, m.frac_year1_appreciation);
  const y5Net = sum3(m.frac_year5_cash, m.frac_year5_revenues, m.frac_year5_appreciation);

  const push = async () => {
    if (!m.property_name.trim()) {
      toast.error("Enter the property name (Financial Model for: …).");
      return;
    }
    if (!email) {
      toast.error("Contact email is required to attach the note in HubSpot.");
      return;
    }
    setPushing(true);
    try {
      const payload = { ...cleanPayload(), contact_email: email };
      const r = await adminApi.post("/admin/financial-model/push", payload);
      toast.success(
        r.data.hubspot_deal_id
          ? "Financial model pushed — note attached to the property's contact & deal."
          : "Financial model pushed — note attached to the contact."
      );
      setM(initialModel);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not push to HubSpot.");
    } finally {
      setPushing(false);
    }
  };

  return (
    <section
      data-testid="financial-model-card"
      className="mt-14 bg-white border border-[#E3DED5]"
    >
      <div className="px-6 lg:px-8 py-5 border-b border-[#E3DED5] flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="eyebrow">Janté Thrive · Deck p.8</div>
          <h2 className="font-serif-display text-2xl text-[#2A1F1A] mt-1">
            Financial Model for :
          </h2>
          <p className="text-sm text-[#6E6E70] mt-1 max-w-2xl">
            Fill the per-property scenario matrix — Sale Today vs. Janté
            Management 10% vs. 50% Fractional Sale — and push it as a formatted
            Note on the property&apos;s HubSpot contact &amp; deal.
          </p>
        </div>
        <button
          type="button"
          onClick={prefill}
          disabled={!canPrefill}
          data-testid="fm-prefill-btn"
          title={
            canPrefill
              ? "Compute the matrix from the Scout brief's asking price & rent"
              : "Enter an asking price in the Scout brief above first"
          }
          className="btn-secondary inline-flex items-center gap-2 px-4 py-2 text-xs uppercase tracking-[0.15em] disabled:opacity-40"
        >
          <Wand2 size={13} />
          Pre-fill from Sasha&apos;s brief
        </button>
      </div>

      <div className="px-6 lg:px-8 py-6 space-y-8">
        {drafts.length > 0 && (
          <div
            data-testid="fm-drafts-bar"
            className="flex items-center gap-3 flex-wrap bg-[#F2EFE9] border border-[#E3DED5] px-4 py-3"
          >
            <FolderOpen size={15} className="text-[#B0895C]" />
            <span className="text-xs uppercase tracking-[0.15em] text-[#6E6E70]">
              Saved sheets
            </span>
            <select
              data-testid="fm-drafts-select"
              value={selectedDraft}
              onChange={(e) => loadDraft(e.target.value)}
              className="editorial-input bg-white border border-[#E3DED5] text-sm px-3 py-1.5 min-w-[260px]"
            >
              <option value="">Select a sheet to load…</option>
              {drafts.map((d) => (
                <option key={d.id} value={d.id}>
                  {`${d.property_name} — ${new Date(d.created_at).toLocaleDateString()}`}
                </option>
              ))}
            </select>
          </div>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <Label className="eyebrow">
              Property name <span className="text-[#C03028]">·</span>
            </Label>
            <Input
              data-testid="fm-property-name"
              value={m.property_name}
              onChange={set("property_name")}
              placeholder="Casa da Lua, Comporta"
              className="editorial-input mt-2"
            />
          </div>
          <div>
            <Label className="eyebrow">
              Owner contact email <span className="text-[#C03028]">·</span>
            </Label>
            <Input
              data-testid="fm-contact-email"
              value={email}
              onChange={set("contact_email")}
              placeholder="owner@example.com"
              type="email"
              className="editorial-input mt-2"
            />
          </div>
        </div>

        {/* Matrix */}
        <div className="overflow-x-auto">
          <table className="w-full min-w-[720px] border-collapse" data-testid="fm-matrix">
            <thead>
              <tr>
                <th className="text-left p-3 bg-[#5B1F1B] text-[#FAF9F6] text-xs uppercase tracking-[0.18em] w-[130px]">
                  Scenario
                </th>
                <th className="text-left p-3 bg-[#5B1F1B] text-[#FAF9F6] text-xs uppercase tracking-[0.18em]">
                  Sale Today
                </th>
                <th className="text-left p-3 bg-[#5B1F1B] text-[#FAF9F6] text-xs uppercase tracking-[0.18em]">
                  Janté Management 10%
                </th>
                <th className="text-left p-3 bg-[#5B1F1B] text-[#FAF9F6] text-xs uppercase tracking-[0.18em]">
                  50% Fractional Sale
                </th>
              </tr>
            </thead>
            <tbody className="text-sm">
              <Row label="Cash Today">
                <Cell label="Sale cash" testid="fm-sale-cash-today" value={m.sale_cash_today} onChange={set("sale_cash_today")} />
                <Cell label="Net" testid="fm-mgmt-net-today" value={m.mgmt_net_today} onChange={set("mgmt_net_today")} />
                <Cell label="Cash" testid="fm-frac-cash-today" value={m.frac_cash_today} onChange={set("frac_cash_today")} />
              </Row>
              <Row label="Year 1 Net">
                <Cell label="Sale cash" testid="fm-sale-year1" value={m.sale_year1_cash} onChange={set("sale_year1_cash")} />
                <Cell label="Net" testid="fm-mgmt-year1" value={m.mgmt_year1_net} onChange={set("mgmt_year1_net")} />
                <td className="border border-[#E3DED5] p-3 align-top">
                  <TripleCell
                    prefix="fm-frac-year1"
                    cash={m.frac_year1_cash}
                    revenues={m.frac_year1_revenues}
                    appreciation={m.frac_year1_appreciation}
                    onCash={set("frac_year1_cash")}
                    onRevenues={set("frac_year1_revenues")}
                    onAppreciation={set("frac_year1_appreciation")}
                    net={y1Net}
                  />
                </td>
              </Row>
              <Row label="Year 5 Net">
                <Cell label="Sales cash" testid="fm-sale-year5" value={m.sale_year5_cash} onChange={set("sale_year5_cash")} />
                <td className="border border-[#E3DED5] p-3 align-top space-y-2">
                  <MiniField label="Net" testid="fm-mgmt-year5" value={m.mgmt_year5_net} onChange={set("mgmt_year5_net")} />
                  <MiniField label="CMV" testid="fm-mgmt-cmv" value={m.mgmt_year5_cmv} onChange={set("mgmt_year5_cmv")} />
                </td>
                <td className="border border-[#E3DED5] p-3 align-top">
                  <TripleCell
                    prefix="fm-frac-year5"
                    cash={m.frac_year5_cash}
                    revenues={m.frac_year5_revenues}
                    appreciation={m.frac_year5_appreciation}
                    onCash={set("frac_year5_cash")}
                    onRevenues={set("frac_year5_revenues")}
                    onAppreciation={set("frac_year5_appreciation")}
                    net={y5Net}
                  />
                </td>
              </Row>
              <Row label="Tax Benefit">
                <td className="border border-[#E3DED5] p-3 align-top">
                  <div className="text-xs text-[#6E6E70] mb-2">− Capital gains tax 25%</div>
                  <MiniField label="Net cash" testid="fm-sale-tax-net" value={m.sale_tax_net_cash} onChange={set("sale_tax_net_cash")} />
                </td>
                <td className="border border-[#E3DED5] p-3 align-top text-xs text-[#2A1F1A]">
                  + Charitable deduction
                  <br />+ Expense deductions
                </td>
                <td className="border border-[#E3DED5] p-3 align-top text-xs text-[#2A1F1A]">
                  + Partial capital gains
                  <br />+ Deductions
                </td>
              </Row>
              <Row label="Legacy">
                <td className="border border-[#E3DED5] p-3 align-top text-xs text-[#6E6E70]">
                  Housing 0 families
                </td>
                <td className="border border-[#E3DED5] p-3 align-top">
                  <MiniField label="Families housed in 5 yrs" testid="fm-mgmt-families" value={m.mgmt_families_5yr} onChange={set("mgmt_families_5yr")} />
                </td>
                <td className="border border-[#E3DED5] p-3 align-top">
                  <div className="text-xs text-[#B0895C] uppercase tracking-wide mb-1.5">Founding partner</div>
                  <MiniField label="Families housed in 5 yrs" testid="fm-frac-families" value={m.frac_families_5yr} onChange={set("frac_families_5yr")} />
                </td>
              </Row>
            </tbody>
          </table>
        </div>

        <div>
          <Label className="eyebrow">Notes (optional)</Label>
          <Textarea
            data-testid="fm-notes"
            value={m.notes}
            onChange={set("notes")}
            placeholder="Assumptions, comps used, owner conversation highlights…"
            className="editorial-input mt-2 min-h-[70px]"
          />
        </div>

        <div className="flex justify-end gap-3 flex-wrap">
          <button
            type="button"
            onClick={saveDraft}
            disabled={saving}
            data-testid="fm-save-btn"
            className="btn-secondary inline-flex items-center justify-center gap-2 px-6 py-2.5 text-sm"
          >
            {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
            {saving ? "Saving…" : "Save sheet"}
          </button>
          <button
            type="button"
            onClick={push}
            disabled={pushing || !hsConfigured}
            data-testid="fm-push-btn"
            title={hsConfigured ? "Push to HubSpot notes" : "Connect HubSpot in Settings first"}
            className="btn-primary inline-flex items-center justify-center gap-2 px-6 py-2.5 text-sm"
          >
            {pushing ? <Loader2 size={14} className="animate-spin" /> : <UploadCloud size={14} />}
            {pushing ? "Pushing…" : "Push model to HubSpot notes"}
          </button>
        </div>
      </div>
    </section>
  );
};

function Row({ label, children }) {
  return (
    <tr className="odd:bg-[#FAF7F2]">
      <td className="border border-[#E3DED5] p-3 align-top font-serif-display text-lg text-[#2A1F1A]">
        {label}
      </td>
      {children}
    </tr>
  );
}

function Cell({ label, testid, value, onChange }) {
  return (
    <td className="border border-[#E3DED5] p-3 align-top">
      <MiniField label={label} testid={testid} value={value} onChange={onChange} />
    </td>
  );
}

function MiniField({ label, testid, value, onChange }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-[0.15em] text-[#6E6E70] mb-1">{label}</div>
      <Input
        data-testid={testid}
        value={value}
        onChange={onChange}
        type="number"
        placeholder="0"
        className="editorial-input h-8 text-sm"
      />
    </div>
  );
}

function TripleCell({ prefix, cash, revenues, appreciation, onCash, onRevenues, onAppreciation, net }) {
  return (
    <div className="space-y-2">
      <div className="grid grid-cols-3 gap-2">
        <MiniField label="Cash" testid={`${prefix}-cash`} value={cash} onChange={onCash} />
        <MiniField label="Revenues" testid={`${prefix}-revenues`} value={revenues} onChange={onRevenues} />
        <MiniField label="10% Appr." testid={`${prefix}-appreciation`} value={appreciation} onChange={onAppreciation} />
      </div>
      <div className="text-xs text-[#2A1F1A]" data-testid={`${prefix}-net`}>
        Net:{" "}
        <span className="font-semibold">
          {net !== null ? `$${net.toLocaleString()}` : "—"}
        </span>{" "}
        <span className="text-[#8E8F90]">(cash + revenues + 10% appreciation)</span>
      </div>
    </div>
  );
}
